import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import {
  Alliance,
  AllianceWithMembers,
} from '../interfaces/alliance.interface';
import { OverviewData } from '../interfaces/overview.interface';
import { Player, PlayerDetails } from '../interfaces/player.interfaces';
import {
  Scan,
  ScanMap,
  ScanMinimal,
} from '../interfaces/scan.interfaces';
import { FieldMap } from '../interfaces/stellar-cartography.interfaces';
import { User } from '../interfaces/user.interface';
import { UserRoles } from '../auth/current-user';
import { ChangeLogEntry } from '../interfaces/changelog.interface';

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

type ApiRole = 'admin' | 'user' | 'none';
type UserPayload = Partial<Omit<User, 'roles'>> & { roles?: Array<UserRoles | ApiRole> };

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private authService: AuthService) {}

  private mapRoleFromApi(role: UserRoles | ApiRole): UserRoles {
    if (typeof role === 'number') {
      return role;
    }
    switch (role) {
      case 'admin':
        return UserRoles.admin;
      case 'user':
        return UserRoles.user;
      default:
        return UserRoles.none;
    }
  }

  private mapRoleToApi(role: UserRoles | ApiRole): ApiRole {
    if (typeof role === 'string') {
      return role;
    }
    switch (role) {
      case UserRoles.admin:
        return 'admin';
      case UserRoles.user:
        return 'user';
      default:
        return 'none';
    }
  }

  private mapUserFromApi(user: User): User {
    if (!user.roles) {
      return user;
    }
    return {
      ...user,
      roles: user.roles.map((role) => this.mapRoleFromApi(role as UserRoles | ApiRole)),
    };
  }

  private mapUserPayloadToApi(user: UserPayload): UserPayload {
    if (!user.roles) {
      return user;
    }
    return {
      ...user,
      roles: user.roles.map((role) => this.mapRoleToApi(role)),
    };
  }

  private async createHeader() {
    if (!this.authService.currentUser?.token) {
      throw new Error('Token not available');
    }
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + this.authService.currentUser?.token,
    };
  }

  async fetchAlliance(id: number): Promise<AllianceWithMembers> {
    const response = await fetch(`${environment.apiUrl}/api/alliance/${id}`, {
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return await response.json();
  }

  async fetchAlliances(): Promise<Alliance[]> {
    const url = `${environment.apiUrl}/api/alliance`;
    const response = await fetch(url, {
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    const alliances = await response.json();
    return alliances;
  }

  async fetchOverview(): Promise<OverviewData> {
    const url = `${environment.apiUrl}/api/overview`;
    const response = await fetch(url, {
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    const data = await response.json();
    return data;
  }

  async fetchUsers() {
    const url = `${environment.apiUrl}/user`;
    const response = await fetch(url, {
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    const users = await response.json();
    return users.map((user: User) => this.mapUserFromApi(user));
  }

  async fetchUser(id: number): Promise<User> {
    const url = `${environment.apiUrl}/user/${id}`;
    const response = await fetch(url, {
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return this.mapUserFromApi(await response.json());
  }

  async postCreateUser(user: UserPayload): Promise<User> {
    const url = `${environment.apiUrl}/user`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.createHeader(),
      body: JSON.stringify(this.mapUserPayloadToApi(user)),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return this.mapUserFromApi(await response.json());
  }

  async postUpdateUser(id: number, user: UserPayload): Promise<User> {
    const url = `${environment.apiUrl}/user/${id}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.createHeader(),
      body: JSON.stringify(this.mapUserPayloadToApi({ id, ...user })),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return this.mapUserFromApi(await response.json());
  }

  async deleteUser(id: number): Promise<void> {
    const url = `${environment.apiUrl}/user/${id}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
  }

  async fetchScans(): Promise<ScanMinimal[]> {
    const url = `${environment.apiUrl}/api/subspace-scan`;
    const response = await fetch(url, {
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return await response.json();
  }

  async fetchScan(id: number): Promise<Scan> {
    const url = `${environment.apiUrl}/api/subspace-scan/${id}`;
    const response = await fetch(url, {
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return await response.json();
  }

  async fetchScanArea(
    startX: number,
    startY: number,
    endX: number,
    endY: number
  ): Promise<ScanMap> {
    const url = `${environment.apiUrl}/api/subspace-scan/area`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.createHeader(),
      body: JSON.stringify({ startX, startY, endX, endY }),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return await response.json();
  }

  async fetchFields(
    startX: number,
    startY: number,
    endX: number,
    endY: number
  ): Promise<FieldMap> {
    const url = `${environment.apiUrl}/api/fields/area`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.createHeader(),
      body: JSON.stringify({ startX, startY, endX, endY }),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return await response.json();
  }

  async fetchPlayer(id: number): Promise<PlayerDetails> {
    const url = `${environment.apiUrl}/api/player/${id}`;
    const response = await fetch(url, {
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return await response.json();
  }

  async fetchPlayers(): Promise<Player[]> {
    const url = `${environment.apiUrl}/api/player`;
    const response = await fetch(url, {
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return await response.json();
  }

  async fetchActivePlayers(): Promise<Player[]> {
    const url = `${environment.apiUrl}/api/player/active`;
    const response = await fetch(url, {
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return await response.json();
  }

  async fetchChangelog(params: {
    type?: string;
    playerId?: number;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<ChangeLogEntry[]> {
    const query = new URLSearchParams();
    if (params.type) {
      query.set('type', params.type);
    }
    if (params.playerId) {
      query.set('playerId', `${params.playerId}`);
    }
    if (params.from) {
      query.set('from', params.from);
    }
    if (params.to) {
      query.set('to', params.to);
    }
    query.set('limit', `${params.limit ?? 100}`);
    query.set('offset', `${params.offset ?? 0}`);

    const url = `${environment.apiUrl}/api/changelog?${query.toString()}`;
    const response = await fetch(url, {
      headers: await this.createHeader(),
    });
    if (!response.ok) {
      throw new ApiError(response.statusText, response.status);
    }
    return await response.json();
  }
}
